// src/components/kanban/RequestLookupSelect.tsx
/**
 * Select con ricerca lato server per collegare un task a una richiesta (RequestLookupItem).
 * La ricerca è debounced (300ms); il filtro client-side di Mantine è disattivato con
 * `filter={({ options }) => options}` perché il backend ha già applicato il filtro testuale.
 */
import { useState } from 'react';
import { Select } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { useRequestLookups } from '../../hooks/use-kanban-columns';

interface RequestLookupSelectProps {
  value?: number;
  onChange: (id: number | undefined) => void;
  withTasksOnly?: boolean;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
}

export default function RequestLookupSelect({
  value,
  onChange,
  withTasksOnly,
  label,
  required,
  disabled,
  placeholder = 'Cerca una richiesta…',
}: RequestLookupSelectProps) {
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearch] = useDebouncedValue(searchValue, 300);

  const { data: results = [], isFetching } = useRequestLookups(debouncedSearch, !!withTasksOnly, value);

  const data = results.map((r) => ({
    value: String(r.id),
    label: r.clientName ? `${r.label} (${r.clientName})` : r.label,
  }));

  return (
    <Select
      label={label}
      required={required}
      disabled={disabled}
      placeholder={isFetching ? 'Ricerca…' : placeholder}
      data={data}
      searchable
      searchValue={searchValue}
      onSearchChange={setSearchValue}
      filter={({ options }) => options}
      value={value != null ? String(value) : null}
      onChange={(v) => onChange(v ? Number(v) : undefined)}
      clearable
      nothingFoundMessage="Nessuna richiesta trovata"
    />
  );
}
